import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Compass, EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [mode, setMode] = useState("email");
  const [loading, setLoading] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);

  useEffect(() => {
    if (retryAfter <= 0) return;

    const id = setInterval(
      () => setRetryAfter((v) => Math.max(0, v - 1)),
      1000
    );

    return () => clearInterval(id);
  }, [retryAfter]);

  const send = async (e) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      toast.error("Please enter your email address.");
      return;
    }

    setEmail(normalizedEmail);
    setLoading(true);

    try {
      const { data } = await api.post("/auth/email/start", {
        email: normalizedEmail,
        purpose: "reset",
      });

      if (data.status === "not_found") {
        toast.error(
          "No account found with this email. Please sign up first."
        );
        return;
      }

      if (data.status === "social_only") {
        toast.error(
          "This account does not have a password. Please sign in with Google or LinkedIn."
        );
        return;
      }

      // Store the exact email being reset so CreatePassword
      // never displays a previous logged-in account.
      sessionStorage.setItem(
        "cgps_email_setup_email",
        normalizedEmail
      );

      setMode("otp");
      setRetryAfter(data.retry_after || 60);

      toast.success("Verification code sent to your email.");
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Unable to send verification code."
      );
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();

    if (!/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit code.");
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post("/auth/email/verify", {
        email: email.trim().toLowerCase(),
        code,
        purpose: "reset",
      });

      sessionStorage.setItem(
        "cgps_email_setup_token",
        data.setup_token
      );

      sessionStorage.setItem(
        "cgps_email_setup_email",
        email.trim().toLowerCase()
      );

      window.location.assign("/create-password");
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Verification failed."
      );
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (retryAfter > 0 || loading) return;

    setLoading(true);

    try {
      const { data } = await api.post("/auth/email/start", {
        email: email.trim().toLowerCase(),
        purpose: "reset",
      });

      if (data.status === "not_found") {
        toast.error(
          "No account found with this email. Please sign up first."
        );
        setMode("email");
        return;
      }

      if (data.status === "social_only") {
        toast.error(
          "This account does not have a password. Please sign in with Google or LinkedIn."
        );
        setMode("email");
        return;
      }

      setRetryAfter(data.retry_after || 60);
      toast.success("A new verification code was sent.");
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Unable to resend the code."
      );
    } finally {
      setLoading(false);
    }
  };

  const changeEmail = () => {
    setMode("email");
    setCode("");
    setRetryAfter(0);
    sessionStorage.removeItem("cgps_email_setup_email");
    sessionStorage.removeItem("cgps_email_setup_token");
  };

  return (
    <div
      className="min-h-screen bg-[#050505] flex flex-col"
      data-testid="forgot-password-page"
    >
      <div className="px-6 py-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <Compass
            size={22}
            weight="duotone"
            className="text-blue-500"
          />
          <span className="font-medium tracking-tight">
            Career GPS{" "}
            <span className="text-blue-500">AI</span>
          </span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          {mode === "otp" ? (
            <form onSubmit={verify} className="space-y-4">
              <button
                type="button"
                onClick={changeEmail}
                className="text-sm text-zinc-400 hover:text-white flex items-center gap-2"
              >
                <ArrowLeft size={14} />
                Change email
              </button>

              <EnvelopeSimple
                size={36}
                weight="duotone"
                className="text-blue-400 mb-3"
              />

              <h1 className="text-3xl font-medium">
                Verify your email
              </h1>

              <p className="text-zinc-500 mt-2">
                Enter the 6-digit code sent to{" "}
                <span className="text-white">{email}</span>.
              </p>

              <input
                inputMode="numeric"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) =>
                  setCode(
                    e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 6)
                  )
                }
                data-testid="forgot-otp-input"
                className="mt-4 w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 text-center tracking-[0.5em] rounded-md px-4 py-4 text-2xl text-white outline-none"
              />

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-3 rounded-lg"
              >
                {loading
                  ? "Verifying..."
                  : "Verify & reset password"}
              </button>

              <button
                type="button"
                disabled={retryAfter > 0 || loading}
                onClick={resend}
                className="w-full text-sm text-blue-400 disabled:text-zinc-600"
              >
                {retryAfter > 0
                  ? `Resend code in ${retryAfter}s`
                  : "Resend code"}
              </button>
            </form>
          ) : (
            <>
              <h1 className="text-4xl tracking-tight font-medium">
                Forgot your password?
              </h1>

              <p className="text-zinc-500 mt-2">
                Enter your account email and we'll send you a
                verification code.
              </p>

              <form
                onSubmit={send}
                className="mt-8 space-y-4"
              >
                <div>
                  <label className="text-xs text-zinc-400 uppercase tracking-widest">
                    Email
                  </label>

                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) =>
                      setEmail(e.target.value)
                    }
                    autoComplete="email"
                    data-testid="forgot-email-input"
                    placeholder="you@example.com"
                    className="mt-1 w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 text-white outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-3 rounded-lg"
                >
                  {loading
                    ? "Checking account..."
                    : "Send verification code"}
                </button>
              </form>

              <p className="mt-6 text-sm text-zinc-500 text-center">
                Remembered it?{" "}
                <Link
                  to="/login"
                  className="text-blue-400 hover:text-blue-300"
                >
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
